import { useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import { routes } from '../routes/routePaths';
import { useEffect, useRef, useState } from 'react';
import { getApplications } from '../services/ApplicationService';
import P from '../components/texts/p/P';
import Image from '../components/image/Image';
import Table from '../components/table/table';
import { APPLICATION_STATUS } from '../utils/Constants';
import Input from '../components/input/Input';
import { IItem, ITableData } from '../types/interfaces';
import { hasContent } from '../utils/Utils';
import { getDashboardColumns } from '../utils/dashboardColumns';
import {
  getBadgeStatus,
  getTooltipText,
  getStatusText,
} from '../utils/applicationStatusUtils';
import Tooltip from '../components/tooltip/Tooltip';
import Span from '../components/texts/span/Span';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import { useDebounce } from 'use-debounce';
import { useOfficers } from '../hooks/useOfficers';
import Icon from '../components/icon/Icon';
import Button from '../components/button/Button';
import FilterModal from '../components/modals/FilterModal';
import Badge from '../components/badge/Badge';
import { getFilters } from '../services/ApplicationService';
import { FilterData } from '../components/modals/FilterModal/FilterModal.types';
import { useWindowSize } from '../hooks/useWindowSize';

type application = {
  cuit: string;
  producerName: string;
  producerLastName: string;
  companyName: string;
  status: string;
};

const FinishedFoldersDashboard = () => {
  const navigate = useNavigate();
  const name = localStorage.getItem('name');
  const { officers } = useOfficers();
  const windowWidth = useWindowSize();
  const [applications, setApplications] = useState<application[]>([]);
  const [tableData, setTableData] = useState<ITableData>();
  const [page, setPage] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [debouncedSearchText] = useDebounce(searchText, 300);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>(
    {}
  );
  const [filterData, setFilterData] = useState<FilterData | null>(null);
  const [isClearFiltersHovered, setIsClearFiltersHovered] =
    useState<boolean>(false);

  const lastRequest = useRef(0);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const response = await getFilters('application');
        if (response.success && 'data' in response) {
          setFilterData(response.data);
        }
      } catch (error) {
        console.error('Error loading filters:', error);
      }
    };
    loadFilters();
  }, []);

  useEffect(() => {
    setIsLoading(true);
    const requestId = Date.now();
    lastRequest.current = requestId;

    // Determinar qué estados buscar
    let statusesToSearch = [
      APPLICATION_STATUS.finished.label,
      APPLICATION_STATUS.rejected.label,
    ];

    // Si hay un filtro de estado aplicado, usar solo ese
    let filtersToUse = activeFilters;
    if (activeFilters.status) {
      // El backend devuelve el id como label (REJ, FIN), no como código numérico
      // Buscar por label directamente
      const statusLabel = activeFilters.status;
      const status = Object.values(APPLICATION_STATUS).find(
        (s) => s.label === statusLabel
      );
      if (status) {
        statusesToSearch = [status.label];
      } else {
        // Si no se encuentra por label, usar directamente el valor (por si acaso)
        statusesToSearch = [statusLabel];
      }
      // Remover el filtro de estado de activeFilters ya que se usa en statuses
      const { status: _, ...filtersWithoutStatus } = activeFilters;
      filtersToUse = filtersWithoutStatus;
    }

    getApplications(page, statusesToSearch, debouncedSearchText, filtersToUse)
      .then((response) => {
        if (lastRequest.current !== requestId) return;
        if (hasContent(response)) {
          setTableData({
            size: response.data.size,
            page: response.data.page,
            totalElements: response.data.totalElements,
            totalPages: response.data.totalPages + 1,
          });

          const newApplications = response.data.content.map((item: IItem) => ({
            cuit: item.cuit,
            id: item.id,
            commercial: (() => {
              const officerCode = (item as any).officer;
              if (!officerCode || officerCode === '-') return 'Sin asignar';
              const officer = officers.find((o) => o.code === officerCode);
              return officer ? officer.label : 'Sin asignar';
            })(),
            companyName: item.company_name,
            producerName: item.producer.first_name,
            producerLastName: item.producer.last_name,
            updateDate: item.updated_date,
            status: item.status.id,
            totalArs: item.total_ars,
            totalUsd: item.total_usd,
            approved_total_ars: item.approved_total_ars,
            approved_total_usd: item.approved_total_usd,
            usuario: item.producer.first_name + ' ' + item.producer.last_name,
            referido: (item as any).referido || 'N/A',
          }));
          setApplications(newApplications);
        } else {
          setApplications([]);
        }
        setIsLoading(false);
        setIsSearching(false);
      })
      .catch(() => {
        setApplications([]);
        setIsLoading(false);
        setIsSearching(false);
      });
  }, [page, debouncedSearchText, JSON.stringify(activeFilters), officers]);

  const handleChangePage = (data: number) => {
    if (data < 0 || data === tableData?.totalPages) {
      return;
    }
    setPage(data);
  };

  const handleRowClicked = (data: any) => {
    navigate(routes.clients + '/' + data.cuit + '/application/' + data.id, {
      state: { status: data.status },
    });
  };

  const baseColumns = getDashboardColumns({
    handleRowClicked,
  });

  const zoom = typeof window !== 'undefined' ? window.devicePixelRatio : 1;
  const isNotebook = windowWidth >= 1024 && windowWidth < 1600;
  const isZoomed = zoom > 1.05; // ~125%
  const isZoomedNotebook = isNotebook && isZoomed;

  if (process.env.NODE_ENV !== 'production') {
    console.log('[FinishedFoldersDashboard] zoom/breakpoints', {
      windowWidth,
      zoom,
      isZoomed,
      isNotebook,
      isZoomedNotebook,
      isSmall: windowWidth < 1024,
      isDesktop: windowWidth >= 1600,
    });
  }

  const columns = baseColumns.map((column) => {
    const { minWidth, ...rest } = column;
    let appliedMinWidth = minWidth;
    let appliedGrow = column.grow || 1;

    if (isZoomed || isZoomedNotebook) {
      if (column.name === 'Referido') {
        appliedMinWidth = '50px';
        appliedGrow = 0.1;
      } else if (column.name === 'Comercial') {
        appliedMinWidth = '20px';
        appliedGrow = 0.1;
      } else if (column.name === 'Razón social') {
        appliedMinWidth = '20px';
        appliedGrow = 0.1;
      } 
    }
    // Reemplazar la columna de Estado con una versión personalizada
    if (column.name === 'Estado') {
      return {
        ...rest,
        minWidth: undefined,
        grow: column.grow || 1,
        cell: (row: any) => {
          const tooltipText = getTooltipText(row?.status);
          const badgeVariant = getBadgeStatus(row?.status);
          const statusText = getStatusText(row?.status);

          return (
            <Card alignItems='center' onClick={() => handleRowClicked(row)}>
              {tooltipText ? (
                <Tooltip
                  position='top'
                  msg={
                    <Span type='2' variant='neutral' modifier='inverted'>
                      {tooltipText}
                    </Span>
                  }
                >
                  <Badge variant={badgeVariant} type='text'>
                    {statusText}
                  </Badge>
                </Tooltip>
              ) : (
                <Badge variant={badgeVariant} type='text'>
                  {statusText}
                </Badge>
              )}
            </Card>
          );
        },
      };
    }

    return {
      ...rest,
      minWidth: undefined,
      grow: column.grow || 1,
    };
  });



  
  const handleSearch = () => {
    setIsSearching(true);
    const requestId = Date.now();
    lastRequest.current = requestId;

    let statusesToSearch = [
      APPLICATION_STATUS.finished.label,
      APPLICATION_STATUS.rejected.label,
    ];

    let filtersToUse = activeFilters;
    if (activeFilters.status) {
      const statusLabel = activeFilters.status;
      const status = Object.values(APPLICATION_STATUS).find(
        (s) => s.label === statusLabel
      );
      if (status) {
        statusesToSearch = [status.label];
      } else {
        statusesToSearch = [statusLabel];
      }

      const { status: _, ...filtersWithoutStatus } = activeFilters;
      filtersToUse = filtersWithoutStatus;
    }

    getApplications(page, statusesToSearch, debouncedSearchText, filtersToUse)
      .then((response) => {
        if (lastRequest.current !== requestId) return;
        if (hasContent(response)) {
          setTableData({
            size: response.data.size,
            page: response.data.page,
            totalElements: response.data.totalElements,
            totalPages: response.data.totalPages + 1,
          });

          let content = response.data.content;

          const newApplications = content.map((item: IItem) => ({
            cuit: item.cuit,
            id: item.id,
            commercial: item.commercial
              ? item.commercial.first_name + ' ' + item.commercial.last_name
              : 'Sin asignar',
            companyName: item.company_name,
            producerName: item.producer.first_name,
            producerLastName: item.producer.last_name,
            updateDate: item.updated_date,
            status: item.status.id,
            totalArs: item.total_ars,
            totalUsd: item.total_usd,
            approved_total_ars: item.approved_total_ars,
            approved_total_usd: item.approved_total_usd,
            usuario: item.producer.first_name + ' ' + item.producer.last_name,
            referido: (item as any).referido || 'N/A',
          }));

          setApplications(newApplications);
        } else {
          setApplications([]);
        }
        setIsLoading(false);
        setIsSearching(false);
      })
      .catch(() => {
        setApplications([]);
        setIsLoading(false);
        setIsSearching(false);
      });
  };

  const handleOpenModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleApplyFilters = (queryParams: Record<string, string>) => {
    setActiveFilters(queryParams);
    setPage(0);
  };

  const handleClearFilters = () => {
    setActiveFilters({});
    setPage(0);
  };

  const getActiveFilterLabels = (): Array<{
    type: string;
    label: string;
    filterKey: string;
  }> => {
    if (!filterData || Object.keys(activeFilters).length === 0) {
      return [];
    }

    const labels: Array<{ type: string; label: string; filterKey: string }> =
      [];

    if (activeFilters.commercialId) {
      const commercial = filterData.commercials.find(
        (c) => c.id.toString() === activeFilters.commercialId
      );
      if (commercial) {
        labels.push({
          type: 'Comercial',
          label: commercial.name,
          filterKey: 'commercialId',
        });
      } else {
        labels.push({
          type: 'Comercial',
          label: 'Desconocido',
          filterKey: 'commercialId',
        });
      }
    }

    if (activeFilters.producerId) {
      const producer = filterData.producers.find(
        (p) => p.id.toString() === activeFilters.producerId
      );
      if (producer) {
        labels.push({
          type: 'Usuario',
          label: producer.name,
          filterKey: 'producerId',
        });
      } else {
        labels.push({
          type: 'Usuario',
          label: 'Desconocido',
          filterKey: 'producerId',
        });
      }
    }

    if (activeFilters.referrerName) {
      const referrer = filterData.referrers.find(
        (r) => r.id.toString() === activeFilters.referrerName
      );
      if (referrer) {
        labels.push({
          type: 'Referido',
          label: referrer.name,
          filterKey: 'referrerName',
        });
      } else {
        labels.push({
          type: 'Referido',
          label: 'Desconocido',
          filterKey: 'referrerName',
        });
      }
    }

    if (activeFilters.status) {
      const status = filterData.status.find(
        (s) => s.id.toString() === activeFilters.status
      );
      if (status) {
        const statusObj = Object.values(APPLICATION_STATUS).find(
          (s) => s.code === status.id
        );
        const statusLabel = statusObj ? statusObj.text : status.name;
        labels.push({
          type: 'Estado',
          label: statusLabel,
          filterKey: 'status',
        });
      } else {
        labels.push({
          type: 'Estado',
          label: 'Desconocido',
          filterKey: 'status',
        });
      }
    }

    return labels;
  };

  const handleRemoveFilter = (filterKey: string) => {
    const newFilters = { ...activeFilters };
    delete newFilters[filterKey];
    setActiveFilters(newFilters);
    setPage(0);
  };

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='90vw'
    >
      <Card display='flex' justifyContent='space-between'>
        <Card display='flex' alignItems='center'>
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Hola, {name}
          </H>
        </Card>
      </Card>
      <Card display='flex' gap='16px' alignItems='center' margin={{ top: '4' }}>
        <Button variant='secondary-1' onClick={handleOpenModal}>
          <Card display='flex' alignItems='center' gap='8px'>
            <Icon
              name='other_filter'
              variant='secondary-1'
              width={20}
              height={20}
            />
            <P variant='secondary-1' withMargin={false}>
              Editar filtros
            </P>
          </Card>
        </Button>
        <Card width='400px' display='flex' gap='16px'>
          <Input
            type='text'
            size='md'
            icons={{ iconLeft: 'search_outline' }}
            placeholder='Buscar cliente'
            onChange={(e) => setSearchText(e.target.value)}
            onKeyup={() => handleSearch()}
          />
        </Card>
      </Card>
      {Object.keys(activeFilters).length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            marginTop: '8px',
            flexWrap: 'wrap',
            backgroundColor: '#F5F7FA',
            padding: '16px 24px',
            borderRadius: '4px',
            border: '1px solid #E5E7EB',
          }}
        >
          {getActiveFilterLabels().map((filter, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {filter.filterKey === 'status' ? (
                <>
                  <P
                    variant='neutral'
                    withMargin={false}
                    type='3'
                    weight='bold'
                  >
                    {filter.type}
                  </P>
                  <div
                    style={{
                      backgroundColor: '#A16108',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      height: '24px',
                    }}
                  >
                    <P
                      variant='neutral'
                      modifier='inverted'
                      withMargin={false}
                      type='3'
                      weight='bold'
                      style={{
                        color: '#FFFFFF',
                        fontSize: '11px',
                        lineHeight: '16px',
                        letterSpacing: '0.3px',
                      }}
                    >
                      {filter.label.toUpperCase()}
                    </P>
                  </div>
                </>
              ) : (
                <>
                  <Icon
                    name='other_filter'
                    variant='secondary-1'
                    width={16}
                    height={16}
                  />
                  <P variant='neutral' withMargin={false} type='3'>
                    <span style={{ fontWeight: 'bold' }}>{filter.type}</span>{' '}
                    {filter.label}
                  </P>
                </>
              )}
            </div>
          ))}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
            }}
            onMouseEnter={() => setIsClearFiltersHovered(true)}
            onMouseLeave={() => setIsClearFiltersHovered(false)}
            onClick={handleClearFilters}
          >
            <Icon
              name='delete_outline'
              variant='neutral'
              width={20}
              height={20}
              style={{ color: activeFilters.status ? '#A16108' : '#585053' }}
            />
            <P
              variant='neutral'
              withMargin={false}
              type='3'
              style={{ color: activeFilters.status ? '#A16108' : '#585053' }}
            >
              Borrar filtros
            </P>
          </div>
        </div>
      )}
      {isSearching || isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : applications.length > 0 ? (
        <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
          <Table
            columns={columns}
            data={applications}
            tableData={tableData}
            handleChangePage={(e: any) => handleChangePage(e)}
            onRowClicked={(e: any) => handleRowClicked(e)}
          />
        </Card>
      ) : searchText !== '' ? (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No se encontraron resultados para el usuario ingresado.
          </P>
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No hay carpetas finalizadas o denegadas.
          </P>
          <P variant='primary' align='center'>
            Las carpetas finalizadas y denegadas aparecerán aquí.
          </P>
        </Card>
      )}
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={handleCloseModal}
        onApplyFilters={handleApplyFilters}
        showCommercialFilter={true}
        showProducerFilter={true}
        showReferrerFilter={true}
        showStatusFilter={true}
        allowedStatusIds={[
          APPLICATION_STATUS.rejected.label,
          APPLICATION_STATUS.finished.label,
        ]}
        initialFilters={activeFilters}
      />
    </Card>
  );
};

export default FinishedFoldersDashboard;